// v10/src/components/wb/SaveWBModal.js
//
// W&B parte 2 — Modal para guardar un cálculo de Peso y Balance.
// Pattern: PostFlightModal (overlay + form + setTimeout para feedback de éxito).
//
// Props:
//   - aircraftId: string
//   - aircraftLabel: string (ej "LV-GTE — Cessna 150")
//   - inputs: objeto con los inputs actuales del cálculo (snapshot al abrir)
//   - currentResult: el resultado calculado en vivo (snapshot al abrir)
//   - onClose: () => void
//   - onSaved: (saved) => void
//   - darkMode: bool
//
// Nota: tomamos snapshot de inputs+currentResult al montar el modal para que
// si algo cambia en el componente padre, el resumen que ve el piloto sea el
// mismo que se va a persistir. El backend RECALCULA con los inputs igual, así
// que la "verdad" final viene del backend.

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Loader, AlertCircle, CheckCircle2, StickyNote, Plane } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import wbService from '../../services/wbService';

const SaveWBModal = ({
  aircraftId,
  aircraftLabel,
  inputs,
  currentResult,
  onClose,
  onSaved,
  darkMode,
}) => {
  const { t } = useTranslation();

  // Snapshot al abrir (no reactivo al padre)
  const snapshotRef = useRef({ inputs, currentResult });

  const [authorizations, setAuthorizations] = useState([]);
  const [loadingAuths, setLoadingAuths] = useState(true);
  const [authsError, setAuthsError] = useState(null);

  const [selectedAuthId, setSelectedAuthId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingAuths(true);
      setAuthsError(null);
      try {
        const r = await wbService.listFlightAuthsForAircraft(aircraftId);
        if (cancelled) return;
        setAuthorizations(r.data || []);
      } catch (err) {
        if (!cancelled) {
          setAuthsError(
            err.response?.data?.message ||
              t('wb_history_load_error', { ns: 'admin' })
          );
        }
      } finally {
        if (!cancelled) setLoadingAuths(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [aircraftId, t]);

  const handleSave = async () => {
    if (!snapshotRef.current.currentResult) {
      setError(t('wb_save_no_result', { ns: 'admin' }));
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const res = await wbService.save({
        aircraft_id: aircraftId,
        flight_auth_id: selectedAuthId || null,
        inputs: snapshotRef.current.inputs,
        notes: notes.trim() || null,
      });
      setSaved(true);
      setTimeout(() => {
        onSaved && onSaved(res.data);
        onClose();
      }, 1300);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          t('wb_save_error', { ns: 'admin' })
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Styles ──────────────────────────────────────────────────────
  const bg     = darkMode ? '#111827' : 'white';
  const text   = darkMode ? 'white' : '#0f172a';
  const sub    = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  const muted  = darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8';
  const border = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';

  const inputStyle = {
    width: '100%',
    padding: '11px 13px',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
    background: darkMode ? 'rgba(255,255,255,0.06)' : '#f8fafc',
    border,
    color: text,
  };

  const result = snapshotRef.current.currentResult;
  const withinEnvelope = !!result?.within_envelope;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 110,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.94, y: 24 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.94, y: 24 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            maxWidth: 460,
            maxHeight: '90vh',
            borderRadius: 24,
            background: bg,
            boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
            border,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '18px 22px 14px',
              borderBottom: border,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 13,
                  background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
                }}
              >
                <Save style={{ width: 18, height: 18, color: 'white' }} />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: text }}>
                  {t('wb_save_title', { ns: 'admin' })}
                </h2>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: sub }}>
                  {aircraftLabel}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9',
                border: 'none',
                borderRadius: 10,
                width: 32,
                height: 32,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: sub,
              }}
            >
              <X style={{ width: 16, height: 16 }} />
            </button>
          </div>

          {/* Body */}
          <div
            style={{
              padding: '16px 22px',
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              overflowY: 'auto',
              flex: 1,
            }}
          >
            {/* Resumen del cálculo */}
            {result && (
              <div
                style={{
                  padding: '12px 14px',
                  borderRadius: 14,
                  background: withinEnvelope
                    ? (darkMode ? 'rgba(34,197,94,0.10)' : 'rgba(220,252,231,0.6)')
                    : (darkMode ? 'rgba(239,68,68,0.10)' : 'rgba(254,226,226,0.6)'),
                  border: withinEnvelope
                    ? '1px solid rgba(34,197,94,0.25)'
                    : '1px solid rgba(239,68,68,0.25)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12,
                    color: sub,
                    fontWeight: 600,
                  }}
                >
                  <span>{t('wb_kpi_total_weight', { ns: 'admin' })}</span>
                  <span style={{ color: text, fontWeight: 800, fontSize: 14 }}>
                    {result.total_weight_lb} lb
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12,
                    color: sub,
                    fontWeight: 600,
                  }}
                >
                  <span>{t('wb_kpi_cg', { ns: 'admin' })}</span>
                  <span style={{ color: text, fontWeight: 800, fontSize: 14 }}>
                    {result.cg_in} in
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: withinEnvelope ? '#16a34a' : '#dc2626',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {withinEnvelope
                    ? <CheckCircle2 style={{ width: 14, height: 14 }} />
                    : <AlertCircle style={{ width: 14, height: 14 }} />}
                  {withinEnvelope
                    ? t('wb_status_within', { ns: 'admin' })
                    : result.envelope_status === 'over_mtow'
                      ? t('wb_status_over_mtow', { ns: 'admin' })
                      : result.envelope_status === 'cg_too_forward'
                        ? t('wb_status_cg_forward', { ns: 'admin' })
                        : t('wb_status_cg_aft', { ns: 'admin' })}
                </div>
              </div>
            )}

            {/* Selector de flight_authorization */}
            <div>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: sub,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginBottom: 6,
                }}
              >
                <Plane style={{ width: 13, height: 13 }} />
                {t('wb_save_associate_to_flight', { ns: 'admin' })}
              </label>
              {loadingAuths ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: sub, fontSize: 13, padding: '8px 0' }}>
                  <Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                  {t('wb_save_loading_auths', { ns: 'admin' })}
                </div>
              ) : authsError ? (
                <div style={{ color: '#ef4444', fontSize: 12 }}>{authsError}</div>
              ) : authorizations.length === 0 ? (
                <div
                  style={{
                    padding: '10px 12px',
                    borderRadius: 10,
                    background: darkMode ? 'rgba(245,158,11,0.10)' : 'rgba(254,243,199,0.5)',
                    border: '1px solid rgba(245,158,11,0.25)',
                    color: darkMode ? '#fbbf24' : '#92400e',
                    fontSize: 12,
                    lineHeight: 1.4,
                  }}
                >
                  {t('wb_save_no_authorizations', { ns: 'admin' })}
                </div>
              ) : (
                <select
                  value={selectedAuthId}
                  onChange={(e) => setSelectedAuthId(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">{t('wb_save_no_association', { ns: 'admin' })}</option>
                  {authorizations.map((a) => (
                    <option key={a.id} value={a.id}>
                      {formatAuthOption(a)}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Notas */}
            <div>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: sub,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginBottom: 6,
                }}
              >
                <StickyNote style={{ width: 13, height: 13 }} />
                {t('wb_save_notes_label', { ns: 'admin' })}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('wb_save_notes_placeholder', { ns: 'admin' })}
                maxLength={500}
                rows={3}
                style={{ ...inputStyle, resize: 'none', lineHeight: 1.5 }}
              />
              <div style={{ fontSize: 10, color: muted, textAlign: 'right', marginTop: 4 }}>
                {notes.length}/500
              </div>
            </div>

            {error && (
              <div
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.2)',
                  color: '#ef4444',
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <AlertCircle style={{ width: 14, height: 14 }} />
                {error}
              </div>
            )}

            {saved && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: 'rgba(34,197,94,0.10)',
                  border: '1px solid rgba(34,197,94,0.25)',
                  color: '#16a34a',
                  fontSize: 13,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <CheckCircle2 style={{ width: 14, height: 14 }} />
                {t('wb_save_success', { ns: 'admin' })}
              </motion.div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '14px 22px',
              borderTop: border,
              display: 'flex',
              gap: 8,
              flexShrink: 0,
            }}
          >
            <button
              onClick={onClose}
              disabled={saving}
              style={{
                flex: 1,
                padding: '11px',
                borderRadius: 12,
                border,
                background: 'transparent',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 700,
                color: sub,
                opacity: saving ? 0.6 : 1,
              }}
            >
              {t('common_cancel', { ns: 'admin' })}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || saved || !result}
              style={{
                flex: 2,
                padding: '11px',
                borderRadius: 12,
                border: 'none',
                background: (saving || saved || !result)
                  ? '#64748b'
                  : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                cursor: (saving || saved || !result) ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 700,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 7,
                boxShadow: (saving || saved || !result) ? 'none' : '0 6px 18px rgba(99,102,241,0.35)',
              }}
            >
              {saving ? (
                <>
                  <Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                  {t('wb_calculating', { ns: 'admin' })}
                </>
              ) : (
                <>
                  <Save style={{ width: 14, height: 14 }} />
                  {t('wb_save_confirm', { ns: 'admin' })}
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </AnimatePresence>
  );
};

function formatAuthOption(a) {
  const date = a.created_at ? new Date(a.created_at) : null;
  const dateStr = date
    ? date.toLocaleString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';
  return `${dateStr}${a.registration ? ' · ' + a.registration : ''}`;
}

export default SaveWBModal;
